/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Active Subscription Item Component - Migrated to Shadcn UI
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Badge, Button, Spinner, useIsMobile } from "@lemonadetraining/web-ui";
import { Link } from "react-router-dom";
import { motion, useAnimation } from "motion/react";
import { IconAlarm, IconClockPause, IconCreditCardOff, IconPlayerPause } from "@tabler/icons-react";
import { toast } from "sonner";
import {
  isUsableTimeZone,
  type SubscriptionItem as CanonicalSubscriptionItem,
} from "@lemonadetraining/contracts";

/* own module */
import { cancelSubscription } from "@lemonadetraining/web-shared";
import { undoMembershipCancel, withdrawMembershipRequest } from "@lemonadetraining/web-shared";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import { getActiveLandingPageForBox, getBox } from "@lemonadetraining/web-shared";
import * as PRODUCTS from "@lemonadetraining/web-shared/constants/ProductConst";
import {
  formatCommitmentDate,
  isLiveSubscriptionStatus,
  isTerminalSubscriptionStatus,
  nextChargeLabel,
  resolveCommitmentStatus,
  resolveRenewalNotice,
  toDate,
  type CommitmentMirrorLike,
} from "@lemonadetraining/web-shared/utils/commitmentStatus";
import { RENEWAL_DISMISSED_STORAGE_KEY } from "@lemonadetraining/web-shared/utils/renewalDismissals";
import type { DunningState } from "@lemonadetraining/contracts";
import CommitmentStatusHeader from "./CommitmentStatusHeader.tsx";
import DunningBanner from "./DunningBanner";
import DunningPausedCard from "./DunningPausedCard";
import RenewalNoticeCard from "./RenewalNoticeCard";
import CardUpdateButton from "./CardUpdateButton";
import { PendingDate } from "../time/PendingDate";
import { useCardUpdateRedirect } from "../../hooks/useCardUpdateRedirect";
import {
  DUNNING_CURED,
  PAYMENT_STATUS_BADGE_GRACE,
  PAYMENT_STATUS_BADGE_PAUSED,
} from "./dunningCopy";
import {
  RENEWAL_KEEP_GOING_DONE,
  RENEWAL_KEEP_GOING_DONE_NO_DATE,
  RENEWAL_KEEP_GOING_DONE_TITLE,
} from "./renewalCopy";
import { CANCEL_ENTRY_LABEL } from "../Membership/CancelFlow/copy";
import CancelConfirmDialog from "../Membership/CancelFlow/CancelConfirmDialog";
import { callableMessage } from "../../lib/callableError";

/**
 * A member cannot have more live commitments than this, so anything older is a
 * term that ended long ago. Uncapped, the list would grow forever on a shared
 * office machine — the same reason the settlement queue caps itself.
 */
const MAX_DISMISSED_RENEWALS = 20;

/** Entries are `${subscriptionId}:${endDateMillis}`. Unreadable store = none. */
function readDismissedRenewals(): string[] {
  try {
    const raw = window.localStorage.getItem(RENEWAL_DISMISSED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
}

/* =================== Subscription item image ============== */
const artCoverVariants = {
  appear: { opacity: 1 },
};

interface ItemImageProps {
  src: string;
}

const ItemImage: React.FC<ItemImageProps> = ({ src }) => {
  const artCoverAnimate = useAnimation();

  return (
    <motion.img
      className="h-[60px] w-[50px] shrink-0 overflow-hidden rounded-md object-cover shadow-sm"
      initial={{ opacity: 0 }}
      src={src}
      variants={artCoverVariants}
      animate={artCoverAnimate}
      transition={{ duration: 0.5 }}
      onLoad={() => artCoverAnimate.start("appear")}
      alt=""
    />
  );
};

/** Plain words for Stripe's status; the raw value never reaches the screen. */
const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  trialing: "Trial",
  past_due: "Payment failed",
  unpaid: "Payment failed",
  incomplete: "Payment not finished",
  incomplete_expired: "Not completed",
  canceled: "Ended",
  paused: "Paused",
};

export const subscriptionStatusLabel = (status?: string): string =>
  (status && STATUS_LABEL[status]) || "Status unknown";

/**
 * Money fields come from the package so their conventions cannot drift —
 * `unit_amount` is MINOR units and nullable on metered prices. The rest stays
 * structural: the canonical price's `product` models neither `metadata` nor
 * `images`, both of which this card has always read.
 */
type CanonicalPrice = CanonicalSubscriptionItem["price"];

interface SubscriptionItem {
  price?: Partial<Pick<CanonicalPrice, "id" | "unit_amount" | "currency">> & {
    product?: {
      name?: string;
      id?: string;
      images?: string[];
      metadata?: {
        productType?: string;
        boxID?: string;
        stripeAccountID?: string;
      };
      boxID?: string;
    };
  };
}

interface SubscriptionData {
  id: string;
  status?: string;
  cancel_at_period_end?: boolean;
  cancel_at?: {
    seconds: number;
    nanoseconds: number;
  };
  /*
   * When the subscription actually stopped, and when the cancellation was
   * requested. Both are written null whenever Stripe reports no value, so
   * every read of them has to survive an absent date.
   */
  ended_at?: {
    seconds: number;
    nanoseconds: number;
  } | null;
  canceled_at?: {
    seconds: number;
    nanoseconds: number;
  } | null;
  created?: {
    seconds: number;
    nanoseconds: number;
  };
  current_period_start?: {
    seconds: number;
    nanoseconds: number;
  };
  current_period_end?: {
    seconds: number;
    nanoseconds: number;
  };
  items?: SubscriptionItem[];
  /** Function-written display mirror. Absent = finalizing OR old-model. */
  commitment?: CommitmentMirrorLike;
  /** Function-written marker that tells those two absences apart. */
  commitmentPending?: boolean;
}

interface ActiveSubscriptionItemProps {
  subscription: SubscriptionData;
}

// Type guard for SubscriptionData
function isValidSubscription(sub: unknown): sub is SubscriptionData {
  return (
    typeof sub === "object" &&
    sub !== null &&
    "id" in sub &&
    typeof (sub as SubscriptionData).id === "string"
  );
}

const ActiveSubscriptionItem: React.FC<ActiveSubscriptionItemProps> = ({ subscription }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [commitmentActionLoading, setCommitmentActionLoading] = useState(false);
  // zone: undefined until the gym read settles; a failed read is no zone.
  const [box, setBox] = useState<{ name: string | null; zone: string | null | undefined }>({
    name: null,
    zone: undefined,
  });
  const [loadingBox, setLoadingBox] = useState(false);
  const [curedRecently, setCuredRecently] = useState(false);

  // Validate subscription data early but don't return yet (hooks must be called first)
  const isValid = isValidSubscription(subscription);

  const subscriptionID = isValid ? subscription.id : "";
  const subscriptionItems = isValid ? subscription.items || [] : [];
  const cancelAtPeriodEnd = isValid ? subscription.cancel_at_period_end || false : false;

  // Check if this is a membership subscription
  const isMembership =
    subscriptionItems.length > 0 &&
    subscriptionItems[0].price?.product?.metadata?.productType ===
      (PRODUCTS as Record<string, unknown>).TYPE?.Membership;

  // Get boxID from product metadata if it's a membership
  const boxID = isMembership
    ? subscriptionItems[0].price?.product?.metadata?.boxID ||
      subscriptionItems[0].price?.product?.boxID
    : null;

  // Fetch box name if it's a membership subscription
  useEffect(() => {
    const fetchBoxName = async () => {
      if (isMembership && boxID) {
        setLoadingBox(true);
        try {
          const doc = (await getBox(boxID)) as {
            name?: string;
            settings?: { timezone?: unknown };
          } | null;
          const zone = doc?.settings?.timezone;
          setBox({ name: doc?.name || null, zone: isUsableTimeZone(zone) ? zone : null });
        } catch (error) {
          console.error("Error fetching box name:", error);
          setBox({ name: null, zone: null });
        } finally {
          setLoadingBox(false);
        }
      }
    };

    fetchBoxName();
  }, [isMembership, boxID]);

  // Only a membership's gym has a zone to wait for; anything else uses the browser.
  const zone = isMembership && boxID ? box.zone : null;
  const datesPending = zone === undefined;
  const fmt = useCallback((date: Date) => formatCommitmentDate(date, zone), [zone]);

  // Both fields ride the document this card already renders, so the header
  // costs no additional reads.
  const membershipItem = isMembership ? subscriptionItems[0] : undefined;
  const validDoc = isValid ? subscription : undefined;
  const commitmentStatus = resolveCommitmentStatus({
    mirror: validDoc?.commitment,
    commitmentPending: validDoc?.commitmentPending,
    status: validDoc?.status,
    startedAt: validDoc?.created,
  });

  const unitAmount = membershipItem?.price?.unit_amount;
  const monthlyPrice =
    typeof unitAmount === "number"
      ? { unitAmount, currency: membershipItem?.price?.currency }
      : null;

  /*
   * A commitment is bounded by a Stripe Subscription Schedule, and cancelling
   * one rewrites that schedule — it never sets `cancel_at_period_end`. Reading
   * Stripe's flag for these memberships is therefore reading a field that is
   * false throughout, which is how a cancelled term kept offering "Cancel
   * plan", promising a next charge and showing no end date at all. Every
   * cancel-related decision below comes off the display mirror instead.
   *
   * Grandfathered memberships (no mirror, no pending marker) are unchanged:
   * they have no schedule, so Stripe's flag is the truth for them.
   */
  const committed = commitmentStatus.kind === "committed" ? commitmentStatus : null;
  const commitmentManaged = commitmentStatus.kind !== "none";
  const cancelAtCommitmentEnd = committed?.cancelAtCommitmentEnd === true;
  const openRequestId = committed?.openRequestId || null;
  const membershipProductUID = membershipItem?.price?.product?.id || null;
  const endingSoon = commitmentManaged ? cancelAtCommitmentEnd : cancelAtPeriodEnd;

  // A membership already set to stop has no next charge to promise — and an
  // upfront term has none either, gated here in the data rather than left to a
  // component's early return.
  const upfrontTerm = committed?.paymentMode === "upfront";
  const nextChargeDate =
    endingSoon || !isValid || upfrontTerm ? null : toDate(subscription.current_period_end);

  const liveStatus = isLiveSubscriptionStatus(subscription.status);
  const terminalStatus = isTerminalSubscriptionStatus(subscription.status);

  // Every dunning surface keys on this member's own mirror: no gym setting, no
  // flag. An ended membership reports none of them — the dunning cron closes
  // the episode on a later pass, so its mirror still reads grace or paused for
  // a while after the contract is already over.
  const dunningState: DunningState = terminalStatus ? "none" : (committed?.dunningState ?? "none");
  const commitmentEndDate = committed?.commitmentEndDate ?? null;
  const commitmentEndMillis = commitmentEndDate?.getTime() ?? null;
  const cardUpdate = useCardUpdateRedirect(subscriptionID);

  // A pause can also end because the contract did: only a live one is cured.
  const previousDunningRef = useRef<DunningState>(dunningState);
  useEffect(() => {
    const previous = previousDunningRef.current;
    previousDunningRef.current = dunningState;
    if (dunningState !== "none" || !liveStatus) {
      setCuredRecently(false);
      return;
    }
    if (previous !== "grace" && previous !== "paused") return;
    setCuredRecently(true);
    toast.success(DUNNING_CURED, {
      description:
        commitmentEndMillis !== null && !datesPending
          ? `Committed until ${fmt(new Date(commitmentEndMillis))}`
          : undefined,
    });
  }, [dunningState, liveStatus, commitmentEndMillis, datesPending, fmt]);

  /*
   * The renewal decision, 30 days out. The server's `renewalWindowActive`
   * alone opens it — the client does no date arithmetic of its own, so the
   * card cannot disagree with the push and email the member already got.
   */
  const renewalNotice = resolveRenewalNotice(
    commitmentStatus,
    validDoc?.status,
    cancelAtCommitmentEnd
  );

  /*
   * Dismissal is client-local: there is no server field for "I read this", and
   * inventing one would be a contract change for a preference nothing else
   * needs. The entry carries the end date, so a term that moves (a dunning
   * pause extends it day for day) resurfaces the notice against the new date.
   */
  const renewalDismissId =
    renewalNotice.kind === "none" || !subscriptionID
      ? null
      : `${subscriptionID}:${renewalNotice.endDate.getTime()}`;
  const commitmentHeaderRef = useRef<HTMLDivElement>(null);
  const [renewalDismissedNow, setRenewalDismissedNow] = useState<string | null>(null);
  const renewalDismissedBefore = useMemo(
    () => (renewalDismissId ? readDismissedRenewals().includes(renewalDismissId) : false),
    [renewalDismissId]
  );
  const renewalDismissed =
    renewalDismissedBefore ||
    (renewalDismissId !== null && renewalDismissedNow === renewalDismissId);
  const showRenewalNotice = renewalNotice.kind !== "none" && !renewalDismissed;

  /*
   * An expiring term needs somewhere to renew, and the only plan picker in the
   * estate is the gym's storefront on the marketing site. Resolved once, and
   * never unset: a button that appears and then vanishes under a reader's
   * cursor is worse than one that arrives a moment late.
   */
  const [renewHref, setRenewHref] = useState<string | null>(null);
  const needsRenewHref = showRenewalNotice && renewalNotice.kind === "expire";
  useEffect(() => {
    if (!needsRenewHref || !boxID) return undefined;
    let abandoned = false;
    getActiveLandingPageForBox(boxID).then((page) => {
      if (abandoned || !page) return;
      const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
      setRenewHref(`${websiteUrl}/landing-page/${page.name}`);
    });
    return () => {
      abandoned = true;
    };
  }, [needsRenewHref, boxID]);

  const onRenewalDismiss = () => {
    if (!renewalDismissId) return;
    try {
      const next = [
        ...readDismissedRenewals().filter((id) => id !== renewalDismissId),
        renewalDismissId,
      ].slice(-MAX_DISMISSED_RENEWALS);
      window.localStorage.setItem(RENEWAL_DISMISSED_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // A full or blocked store must not trap the member on the card; hiding
      // it for this session is still the right answer.
    }
    setRenewalDismissedNow(renewalDismissId);
    // The button the member just pressed is about to unmount. Hand focus to
    // the term summary first, or it falls to <body> and the next Tab restarts
    // at the top of the page — and on the expire path there is no toast to
    // announce anything either.
    commitmentHeaderRef.current?.focus();
    if (renewalNotice.kind !== "roll") return;
    dispatch(
      notifyActions.sendNotification({
        severity: NOTIFICATION_TYPE.success,
        title: RENEWAL_KEEP_GOING_DONE_TITLE,
        message: datesPending
          ? RENEWAL_KEEP_GOING_DONE_NO_DATE
          : RENEWAL_KEEP_GOING_DONE(fmt(renewalNotice.endDate)),
      })
    );
  };

  // connected stripe ID where the subscription belongs to
  const stripeAccountID =
    (subscription.items &&
      subscription.items.length > 0 &&
      subscription.items[0].price?.product?.metadata?.stripeAccountID) ||
    null;

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const planEndDate = toDate(subscription.cancel_at) ?? toDate(subscription.current_period_end);
  const planName = subscriptionItems[0]?.price?.product?.name || "this plan";

  const onCancelSubscription = () => {
    setLoading(true);
    cancelSubscription(subscriptionID, stripeAccountID)
      .then(() => {
        setCancelDialogOpen(false);
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.success,
            title: "Plan cancelled",
            message:
              planEndDate && !datesPending
                ? `It ends on ${fmt(planEndDate)}. You keep access until then.`
                : "It ends at the end of the current period.",
          })
        );
      })
      .catch(() => {
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.error,
            title: "Not cancelled",
            message: "We couldn't cancel your plan. Please try again.",
          })
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /*
   * The two reversals a committed membership offers. Both are server-guarded
   * and both rewrite the mirror, so nothing is set optimistically here — the
   * listener on this document delivers the new state.
   */
  const runCommitmentAction = (
    action: () => Promise<unknown>,
    successTitle: string,
    successMessage: string,
    failureTitle: string
  ) => {
    setCommitmentActionLoading(true);
    action()
      .then(() => {
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.success,
            title: successTitle,
            message: successMessage,
          })
        );
      })
      .catch((error: unknown) => {
        dispatch(
          notifyActions.sendNotification({
            severity: NOTIFICATION_TYPE.error,
            title: failureTitle,
            // The server's own words when it wrote them for the reader; never an SDK string.
            message: callableMessage(error) || "Please try again in a moment.",
          })
        );
      })
      .finally(() => setCommitmentActionLoading(false));
  };

  const onKeepMembership = () => {
    if (!membershipProductUID) return;
    runCommitmentAction(
      () => undoMembershipCancel(subscriptionID, membershipProductUID),
      "Membership kept",
      "Your membership will carry on as before.",
      "We could not keep your membership"
    );
  };

  const onWithdrawRequest = () => {
    if (!openRequestId) return;
    runCommitmentAction(
      () => withdrawMembershipRequest(openRequestId),
      "Request withdrawn",
      "Your gym is no longer waiting on this.",
      "We could not withdraw that request"
    );
  };

  type BadgeVariant = "default" | "secondary" | "destructive" | "outline";
  const badgeVariant: BadgeVariant =
    subscription.status === "past_due" || subscription.status === "unpaid"
      ? "destructive"
      : subscription.status === "active"
        ? "default"
        : subscription.status === "canceled"
          ? "outline"
          : "secondary";

  const dunningBadge =
    dunningState === "paused"
      ? { label: PAYMENT_STATUS_BADGE_PAUSED, Icon: IconPlayerPause }
      : dunningState === "grace" && subscription.status === "past_due"
        ? { label: PAYMENT_STATUS_BADGE_GRACE, Icon: IconCreditCardOff }
        : null;

  // Return null for invalid subscription (after all hooks have been called)
  if (!isValid) {
    return null;
  }

  /*
   * The cancel entry is offered for as long as the membership is
   * commitment-shaped, INCLUDING while it is still finalizing. A hidden cancel
   * button is precisely what § 312k sanctions, so the entry stands and the
   * server gives an honest, retryable refusal if provisioning genuinely has
   * not finished. Nothing here consults a gym setting or a feature flag —
   * only this athlete's own subscription document. The single exception is a
   * TERMINAL subscription: there is nothing left to cancel, so offering the
   * entry would promise an action the server can only refuse.
   */
  // The card-update entry is permanent in every dunning state of a live
  // membership; it never hides behind urgency, and an ended one has no card to update.
  const showPaymentMethodUpdate = isMembership && liveStatus && subscriptionID.length > 0;

  const endedDate = toDate(subscription.ended_at) ?? toDate(subscription.canceled_at);

  const commitmentActions =
    !terminalStatus && (commitmentManaged || showPaymentMethodUpdate) ? (
      <div className="flex flex-col gap-2" data-testid="commitment-actions">
        {committed && cancelAtCommitmentEnd && (
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground"
              data-testid="commitment-scheduled-end"
            >
              <IconAlarm aria-hidden="true" className="size-4" />
              Scheduled to end {datesPending ? <PendingDate /> : fmt(committed.commitmentEndDate)}
            </span>
            <Button
              variant="outline"
              className="min-h-[44px] px-4"
              onClick={onKeepMembership}
              disabled={commitmentActionLoading}
              aria-busy={commitmentActionLoading}
              data-testid="commitment-keep"
            >
              {commitmentActionLoading && <Spinner className="h-4 w-4" />}
              Keep my membership
            </Button>
          </div>
        )}

        {openRequestId && (
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="gap-1.5" data-testid="commitment-request-pending">
              <IconClockPause aria-hidden="true" className="size-3.5" />
              Request pending
            </Badge>
            <Button
              variant="outline"
              className="min-h-[44px] px-4"
              onClick={onWithdrawRequest}
              disabled={commitmentActionLoading}
              aria-busy={commitmentActionLoading}
              data-testid="commitment-withdraw"
            >
              {commitmentActionLoading && <Spinner className="h-4 w-4" />}
              Withdraw request
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {commitmentManaged && !cancelAtCommitmentEnd && (
            <Button
              asChild
              variant="outline"
              className="min-h-[44px] px-4"
              data-testid="commitment-cancel-entry"
            >
              <Link to={`/dashboard/membership/${subscriptionID}/cancel/reason`}>
                {CANCEL_ENTRY_LABEL}
              </Link>
            </Button>
          )}
          {showPaymentMethodUpdate ? (
            <CardUpdateButton
              cardUpdate={cardUpdate}
              variant="secondary"
              className="min-h-[44px] px-4"
              testId="payment-method-update"
              showError={dunningState === "none"}
            />
          ) : null}
        </div>
      </div>
    ) : null;

  // Checkout and subscription.created are independent Stripe deliveries. When
  // checkout wins, the document exists carrying only the pending marker — no
  // status, no items, nothing to label a trigger with. An accordion here would
  // be an unnamed button, so the state gets a plain card instead. Hiding the
  // row is not an option: it is the athlete's just-purchased membership.
  if (subscriptionItems.length === 0 && commitmentStatus.kind !== "none") {
    return (
      <div
        className="flex flex-col gap-2 border rounded-lg px-4 py-3"
        data-testid={`subscription-item-${subscriptionID}`}
      >
        <CommitmentStatusHeader
          status={commitmentStatus}
          ended={terminalStatus ? { date: endedDate } : undefined}
          timeZone={zone}
          datesPending={datesPending}
        />
        {commitmentActions}
      </div>
    );
  }

  const statusBadge = (() => {
    if (dunningBadge) {
      return (
        <Badge
          variant="secondary"
          className="shrink-0 gap-1.5"
          data-testid="subscription-status-badge"
        >
          <dunningBadge.Icon aria-hidden="true" className="size-3.5" />
          {dunningBadge.label}
        </Badge>
      );
    }
    const endsOn = commitmentManaged ? commitmentEndDate : planEndDate;
    if (!terminalStatus && endingSoon && endsOn) {
      if (datesPending) return null;
      return (
        <Badge variant="outline" className="shrink-0" data-testid="subscription-status-badge">
          Ends {fmt(endsOn)}
        </Badge>
      );
    }
    return (
      <Badge variant={badgeVariant} className="shrink-0" data-testid="subscription-status-badge">
        {subscriptionStatusLabel(subscription.status)}
      </Badge>
    );
  })();

  // Grandfathered memberships and every non-membership subscription. A
  // commitment cannot be ended by a direct Stripe cancel (the schedule would
  // revert it at the next phase boundary), so it gets the lane flow instead.
  const canCancelPlan =
    !commitmentManaged && !cancelAtPeriodEnd && subscription.status === "active";
  const periodLine =
    commitmentManaged || terminalStatus
      ? null
      : cancelAtPeriodEnd
        ? planEndDate && !datesPending
          ? `Ends ${fmt(planEndDate)}. You keep access until then.`
          : null
        : nextChargeDate
          ? nextChargeLabel(nextChargeDate)
          : null;

  /* ------- [Returns] --------- */
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border px-4 py-3"
      data-testid={`subscription-item-${subscriptionID}`}
    >
      <div className="flex items-start gap-3">
        {!isMobile && subscriptionItems[0]?.price?.product?.images?.[0] && (
          <ItemImage src={subscriptionItems[0].price.product.images[0]} />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          {subscriptionItems.map((item, idx) => (
            <span key={idx} className="text-sm font-medium">
              {item.price?.product?.name || "Unnamed plan"}
            </span>
          ))}
          {isMembership && box.name && (
            <span className="text-xs text-muted-foreground" data-testid="subscription-gym-name">
              {box.name}
            </span>
          )}
          {isMembership && loadingBox && <Spinner className="h-3 w-3" />}
        </div>
        {statusBadge}
      </div>

      {(isMembership || commitmentManaged) && (
        <div className="flex flex-col gap-2">
          <CommitmentStatusHeader
            ref={commitmentHeaderRef}
            status={commitmentStatus}
            ended={terminalStatus ? { date: endedDate } : undefined}
            timeZone={zone}
            datesPending={datesPending}
            monthlyPrice={monthlyPrice}
            nextChargeDate={nextChargeDate}
          />
          {curedRecently && dunningState === "none" ? (
            <p role="status" className="text-sm text-foreground" data-testid="dunning-cured">
              {DUNNING_CURED}
            </p>
          ) : null}
          {dunningState === "grace" ? (
            <DunningBanner
              graceEndsAt={committed?.graceEndsAt ?? null}
              cardUpdate={cardUpdate}
              timeZone={zone}
              datesPending={datesPending}
            />
          ) : null}
          {dunningState === "paused" && commitmentEndDate ? (
            <DunningPausedCard
              commitmentEndDate={commitmentEndDate}
              pausedAt={committed?.pausedAt ?? null}
              cardUpdate={cardUpdate}
              timeZone={zone}
              datesPending={datesPending}
            />
          ) : null}
          {showRenewalNotice && renewalNotice.kind !== "none" ? (
            <RenewalNoticeCard
              notice={renewalNotice}
              membershipName={membershipItem?.price?.product?.name || "membership"}
              subscriptionId={subscriptionID}
              renewHref={renewHref}
              onDismiss={onRenewalDismiss}
              timeZone={zone}
              datesPending={datesPending}
            />
          ) : null}
          {commitmentActions}
        </div>
      )}

      {periodLine && (
        <span className="text-xs text-muted-foreground" data-testid="subscription-period-line">
          {periodLine}
        </span>
      )}

      {canCancelPlan && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="min-h-[44px] px-4"
            onClick={() => setCancelDialogOpen(true)}
            data-testid="subscription-cancel-plan"
          >
            Cancel plan
          </Button>
        </div>
      )}

      <CancelConfirmDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        title={`Cancel ${planName}?`}
        consequence={
          planEndDate && !datesPending
            ? `Your plan ends on ${fmt(planEndDate)}. You keep access until then, and nothing more is charged.`
            : "Your plan ends at the end of the current period. You keep access until then, and nothing more is charged."
        }
        confirmLabel="Cancel plan"
        onConfirm={onCancelSubscription}
        loading={loading}
        testId="subscription-cancel-dialog"
      />
    </div>
  );
};

export default ActiveSubscriptionItem;
export type { SubscriptionData };
